import { useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useMapStore } from '../lib/map/store';
import { GoogleMapsGeocoderAddressComponent, GoogleMapsGeocoderResult } from '../types/google-maps';

// Define types for location data
interface LocationData {
  address: string;
  coords?: { lat: number; lng: number };
  loading: boolean;
  error?: string;
}

// Track if Google Maps API is being loaded
let isLoadingGoogleMapsAPI = false;
let googleMapsLoadPromise: Promise<void> | null = null;

// Function to load Google Maps API (singleton pattern)
const loadGoogleMapsAPI = () => {
  // If API is already loaded, resolve immediately
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }
  
  // For actual API loading, we'll use the method from the store
  return useMapStore.getState().loadGoogleMapsAPI ? 
    useMapStore.getState().loadGoogleMapsAPI() : 
    Promise.reject(new Error('Google Maps API loader not available'));
};

export const useLocationManager = () => {
  // Get store methods for location handling
  const { 
    currentLocation,
    setCurrentLocation, 
    getCurrentLocation: storeGetCurrentLocation,
    handleCurrentLocationSelect: storeHandleCurrentLocationSelect
  } = useMapStore();
  
  // Function to handle current location selection
  const handleCurrentLocationSelect = useCallback(async () => {
    try {
      const coords = await storeHandleCurrentLocationSelect();
      return coords;
    } catch (err) {
      console.error('Error handling current location:', err);
      toast.error('Could not get your current location');
      return null;
    }
  }, [storeHandleCurrentLocationSelect]);
  
  // Create a direct pass-through to store's getCurrentLocation
  const getCurrentLocation = useCallback(async () => {
    return await storeGetCurrentLocation();
  }, [storeGetCurrentLocation]);
  
  // Function to geocode a position to an address (using store methods)
  const geocodePosition = useCallback(async (position: { lat: number; lng: number }) => {
    try {
      const result = await useMapStore.getState().geocodePosition(position);
      return {
        address: result.address,
        locationName: result.locationName
      };
    } catch (error) {
      console.error('Error geocoding position:', error);
      return {
        address: 'Address information unavailable',
        locationName: 'Unknown location'
      };
    }
  }, []);
  
  // Return the same interface as before, but now backed by the store
  return {
    currentLocation,
    setCurrentLocation: (data: any) => setCurrentLocation(data),
    getCurrentLocation,
    geocodePosition,
    handleCurrentLocationSelect,
    loadGoogleMapsAPI
  };
}; 